import * as React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Sparkles, 
  Camera, 
  Package, 
  ArrowRight, 
  X, 
  MapPin, 
  Layers, 
  RotateCcw,
  BookOpen
} from 'lucide-react';
import { Button } from './ui/button';
import { useStore } from '../store/useStore';

interface OnboardingTourProps {
  itemsCount: number;
}

export default function OnboardingTour({ itemsCount }: OnboardingTourProps) {
  const { babyProfile } = useStore();
  const caregiverName = babyProfile?.caregiverName || 'Mama';
  
  const [currentStep, setCurrentStep] = React.useState<number>(0); // 0 = not started or closed, 1 = Step 1 (Pantry), 2 = Step 2 (Foto Bahan), 3 = Step 3 (Info Mulai)

  const [hasCompletedTour, setHasCompletedTour] = React.useState<boolean>(false);
  const [mounted, setMounted] = React.useState<boolean>(false);
  const [coordsPantry, setCoordsPantry] = React.useState<{ x: number, y: number } | null>(null);
  const [coordsCamera, setCoordsCamera] = React.useState<{ x: number, y: number } | null>(null);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const measureTargets = React.useCallback(() => {
    const pantryEl = document.getElementById('tour-pantry-btn');
    if (pantryEl) {
      const r = pantryEl.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = r.top + r.height / 2;
      setCoordsPantry({ x, y });
    }
    const cameraEl = document.getElementById('tour-camera-btn');
    if (cameraEl) {
      const r = cameraEl.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = r.top + r.height / 2;
      setCoordsCamera({ x, y });
    }
  }, []);

  React.useEffect(() => {
    // Check local storage so we only show this automatically if they haven't completed it
    let completed = null;
    try {
      completed = localStorage.getItem('satu-wajan-tour-completed');
    } catch(e) {}
    if (completed === 'true') {
      setHasCompletedTour(true);
    } else if (itemsCount === 0) {
      // Automatically trigger tour after a slight delay for first-time users with empty shelf
      const timer = setTimeout(() => {
        setCurrentStep(1);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [itemsCount]);

  React.useEffect(() => {
    if (currentStep > 0) {
      measureTargets();
      window.addEventListener('resize', measureTargets);
      window.addEventListener('scroll', measureTargets);
      
      // Layouts can shift when data is rendering, check coordinates periodically during active tour
      const interval = setInterval(measureTargets, 200);
      return () => {
        window.removeEventListener('resize', measureTargets);
        window.removeEventListener('scroll', measureTargets);
        clearInterval(interval);
      };
    }
  }, [currentStep, measureTargets]);

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setCurrentStep(0);
    setHasCompletedTour(true);
    try {
      localStorage.setItem('satu-wajan-tour-completed', 'true');
    } catch(e) {}
  };

  const handleResetTour = () => {
    setCurrentStep(1);
    setHasCompletedTour(false);
    try {
      localStorage.removeItem('satu-wajan-tour-completed');
    } catch(e) {}
  };

  // If tour has already been completed and they are not actively doing it, we show a minor "Bantuan Panduan" re-trigger button optionally,
  // but to prevent visual noise we'll only show the dynamic tooltips/overlays when tour is active.
  if (itemsCount > 0 && currentStep === 0) return null;

  return (
    <>
      {/* If completed, we show a small organic float banner only if their fridge is still empty, reminding them lovingly */}
      {hasCompletedTour && itemsCount === 0 && (
        <motion.div 
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto bg-amber-50/90 border border-amber-200/60 rounded-3xl p-4 shadow-sm backdrop-blur-sm flex items-center justify-between gap-3 mt-2 mb-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500 rounded-2xl text-white">
              <Sparkles className="w-4 h-4 animate-pulse" />
            </div>
            <div className="text-left">
              <p className="text-xs font-bold text-stone-900 leading-tight">Kulkas Masih Kosong</p>
              <p className="text-[10px] text-stone-500 font-semibold leading-tight">Gunakan Foto Bahan AI atau manual agar bisa cari resep!</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleResetTour}
            className="text-[10px] font-bold text-amber-700 hover:text-amber-800 uppercase tracking-wider shrink-0 gap-1"
          >
            <RotateCcw className="w-3 h-3" /> Panduan
          </Button>
        </motion.div>
      )}

      {mounted && typeof document !== 'undefined' ? createPortal(
        <AnimatePresence>
          {currentStep > 0 && (
            <div className="fixed inset-0 z-[100] pointer-events-none">
              {/* Backdrop highlights (semi-transparent mask to draw focus) */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.4 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-stone-950 pointer-events-auto"
                onClick={handleComplete}
              />

              {/* Stepper Card */}
              <div className="absolute inset-x-0 bottom-28 max-w-md mx-auto px-5 pointer-events-auto">
                <motion.div 
                  key={`onboarding-card-${currentStep}`}
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 30, scale: 0.95 }}
                  className="bg-white rounded-[2rem] p-6 shadow-2xl border border-stone-100 flex flex-col relative overflow-hidden"
                >
                  {/* Close Button */}
                  <button 
                    onClick={handleComplete}
                    className="absolute top-4 right-4 p-1.5 hover:bg-stone-50 rounded-full transition-colors text-stone-400"
                  >
                    <X className="w-4 h-4" />
                  </button>

                  {/* Header Info */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="px-2.5 py-1 bg-amber-500 text-white rounded-full text-[9px] font-extrabold uppercase tracking-widest">
                      Langkah {currentStep} dari 3
                    </div>
                    <div className="flex gap-1">
                      {[1, 2, 3].map((step) => (
                        <div 
                          key={step} 
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            currentStep === step ? 'w-4 bg-amber-500' : 'w-1.5 bg-stone-150'
                          }`} 
                        />
                      ))}
                    </div>
                  </div>

                  <AnimatePresence mode="wait">
                    {currentStep === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-2 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-stone-900 text-white rounded-2xl">
                            <Package className="w-5 h-5" />
                          </div>
                          <h4 className="font-bold text-stone-900 text-base">Kulkas Virtual Kosong</h4>
                        </div>
                        <p className="text-xs text-stone-500 font-medium leading-relaxed pt-2">
                          Ini adalah <strong className="text-amber-600">Virtual Kulkas</strong> {caregiverName} di pojok kanan atas. Di sini, {caregiverName} mencatat semua stok bahan makanan yang tersisa di rumah agar asisten AI kami bisa memandu resep makanan dengan persisi tinggi bebas mubazir.
                        </p>
                      </motion.div>
                    )}

                    {currentStep === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-2 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-amber-500 text-white rounded-2xl">
                            <Camera className="w-5 h-5 animate-bounce" />
                          </div>
                          <h4 className="font-bold text-stone-900 text-base">Foto Bahan & Kulkas</h4>
                        </div>
                        <p className="text-xs text-stone-500 font-medium leading-relaxed pt-2">
                          Paling malas mencatat satu per satu? <strong className="text-amber-600">Gunakan Foto Bahan AI!</strong> Cukup jepret isi kulkas atau meja masak {caregiverName}, asisten AI kami akan secara ajaib mengenali, memilah kategori gizi, dan mendaftarkannya langsung.
                        </p>
                      </motion.div>
                    )}

                    {currentStep === 3 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-2 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-green-500 text-white rounded-2xl">
                            <Sparkles className="w-5 h-5" />
                          </div>
                          <h4 className="font-bold text-stone-900 text-base">Input Manual Cepat</h4>
                        </div>
                        <p className="text-xs text-stone-500 font-medium leading-relaxed pt-2">
                          {caregiverName} juga bisa mengklik ikon kulkas untuk mengetik manual dengan mudah di halaman kulkas untuk memperbarui, mengubah jumlah, atau menghapus item persediaan bahan makanan {caregiverName} dengan cepat.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Footer Controls */}
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-stone-50">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleComplete} 
                      className="text-stone-400 text-xs hover:text-stone-600"
                    >
                      Lewati
                    </Button>
                    <div className="flex items-center gap-2">
                      {currentStep > 1 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handlePrev}
                          className="rounded-xl font-bold text-xs text-stone-600 border-stone-200"
                        >
                          Kembali
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        onClick={handleNext}
                        className="rounded-xl font-bold bg-stone-900 text-white hover:bg-stone-800 text-xs px-4"
                      >
                        {currentStep === 3 ? 'Mulai Sekarang' : 'Lanjut'} 
                        <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Concentric Pulsing Anchors pointing to active items on Page */}
              {currentStep === 1 && coordsPantry && (
                <div 
                  className="absolute z-[100] pointer-events-none flex flex-col items-center justify-center animate-pulse"
                  style={{
                    top: `${coordsPantry.y}px`,
                    left: `${coordsPantry.x}px`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <div className="w-5 h-5 bg-amber-500 rounded-full border-2 border-white shadow-lg animate-ping absolute" />
                  <div className="w-5 h-5 bg-amber-500 rounded-full border-2 border-white shadow-lg relative z-20" />
                  <div className="bg-amber-500 text-white font-extrabold text-[8px] px-2 py-0.5 rounded-full mt-7 shadow-md uppercase tracking-wider relative z-20 whitespace-nowrap">Kulkas</div>
                </div>
              )}

              {currentStep === 2 && coordsCamera && (
                <div 
                  className="absolute z-[100] pointer-events-none flex flex-col items-center justify-center animate-pulse"
                  style={{
                    top: `${coordsCamera.y}px`,
                    left: `${coordsCamera.x}px`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <div className="w-6 h-6 bg-amber-500 rounded-full border-2 border-white shadow-lg animate-ping absolute" />
                  <div className="w-5 h-5 bg-amber-500 rounded-full border-2 border-white shadow-lg relative z-20" />
                  <div className="bg-amber-500 text-white font-extrabold text-[8px] px-2 py-0.5 rounded-full mt-7 shadow-md uppercase tracking-wider relative z-20 whitespace-nowrap">Foto Bahan AI</div>
                </div>
              )}

              {currentStep === 3 && coordsPantry && (
                <div 
                  className="absolute z-[100] pointer-events-none flex flex-col items-center justify-center animate-pulse"
                  style={{
                    top: `${coordsPantry.y}px`,
                    left: `${coordsPantry.x}px`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <div className="w-5 h-5 bg-amber-500 rounded-full border-2 border-white shadow-lg animate-ping absolute" />
                  <div className="w-5 h-5 bg-amber-500 rounded-full border-2 border-white shadow-lg relative z-20" />
                </div>
              )}
            </div>
          )}
        </AnimatePresence>,
        document.body
      ) : null}
    </>
  );
}
